import BasicDomain from "./BasicDomain";
import CommonUtils from "../utils/CommonUtils";
import * as yup from 'yup';

export default class Observation extends BasicDomain {
    static DEFAULTS = {
        name: null,
        label: null,
        substantiation: null,
        locDeterminization: null,
        substrate: null,
        quality: null,
        location: null,
        observedBy: null,
        reviewedBy: null,
        flag: false,
        observationRestriction: false,
        lat: null,
        lng: null,
        date: null,
        isDetected: true,
        event: null,
        town: null,
        evidence: [],
        keyword: null,
        dateSpecificity: 'yyyy-mm-dd'
    }

    static FIELDS = [
        { field: 'name', label: 'Name', type: { type: 'Pointer', targetClass: 'Name' } },
        { field: 'label', label: 'Label', type: 'String' },
        { field: 'substantiation', label: 'Substantiation', type: { type: 'Pointer', targetClass: 'Substantiation' } },
        { field: 'locDeterminization', label: 'Location Determined Method', type: { type: 'Pointer', targetClass: 'LocDeterminization' } },
        { field: 'substrate', label: 'Substrate', type: { type: 'Pointer', targetClass: 'Substrate' } },
        { field: 'quality', label: 'Quality', type: { type: 'Pointer', targetClass: 'ObservationQuality' } },
        { field: 'location', label: 'Location', type: { type: 'Pointer', targetClass: 'Location' } },
        { field: 'observedBy', label: 'Observed By', type: { type: 'Pointer', targetClass: 'Person' } },
        { field: 'reviewedBy', label: 'Reviewed By', type: { type: 'Pointer', targetClass: 'Person' } },
        { field: 'flag', label: 'Flag', type: 'Boolean' },
        { field: 'observationRestriction', label: 'Observation Restriction', type: 'Boolean' },
        { field: 'lat', label: 'Latitude', type: 'Number' },
        { field: 'lng', label: 'Longitude', type: 'Number' },
        { field: 'date', label: 'Date', type: 'Date' },
        { field: 'isDetected', label: 'Is Detected', type: 'Boolean' },
        { field: 'event', label: 'Event', type: { type: 'Pointer', targetClass: 'Event' } },
        { field: 'town', label: 'Town', type: { type: 'Pointer', targetClass: 'Town' } },
        { field: 'keyword', label: 'Keyword', type: { type: 'Pointer', targetClass: 'Keyword' } }
    ];
  
    static VALIDATION_SCHEMA = yup.object().shape({
        name: yup.string().required('Name is required'),
        observedBy: yup.string().required('Observer is required'),
        isDetected: yup.boolean().required(),
        flag: yup.boolean().required(),
        date: yup.date()
            .nullable()
            .typeError('Date must be a valid date format (e.g., YYYY-MM-DD or MM/DD/YYYY)'),
        event: yup.string().nullable(),
        location: yup.string().nullable(),
        lat: yup.number()
            .nullable()
            .typeError('Latitude must be a valid number')
            .min(-90, 'Latitude must be between -90 and 90')
            .max(90, 'Latitude must be between -90 and 90'),
        lng: yup.number()
            .nullable()
            .typeError('Longitude must be a valid number')
            .min(-180, 'Longitude must be between -180 and 180')
            .max(180, 'Longitude must be between -180 and 180')
    }).test(
        'dateOrEvent',
        'Either date or event must be provided',
        function(value) {
            if (!value.date && !value.event) {
                return this.createError({
                    path: value.date ? 'event' : 'date',
                    message: 'Either date or event must be provided'
                });
            }
            return true;
        }
    ).test(
        'locationOrCoordinates',
        'Either location or coordinates must be provided',
        function(value) {
            if (!value.location && (!value.lat || !value.lng)) {
                return this.createError({
                    path: value.location ? 'lat' : 'location',
                    message: 'Either location or coordinates must be provided'
                });
            }
            return true;
        }
    );
  
    constructor(props) {
        super('Observation', props, Observation.DEFAULTS);
    }
  
    getTableValues = () => {
        const getLatLng = (type) => {
            if (this.get(type)) {
                return this.get(type);
            } else if (this.get('location') && this.get('location').get('coordinates')) {
                const latLng = {
                    lat: this?.get('location')?.get('coordinates')['_latitude'] || null,
                    lng: this?.get('location')?.get('coordinates')['_longitude'] || null
                };
                return latLng[type];
            }
            return {
                lat: null,
                lng: null
            };
        }
        const date = this.get('event')?.get('startDate') || this.get('date');
        return {
            name: this.get('name')?.name || '',
            location: this.get('location')?.get('label'),
            locationParent: this.get('location')?.get('parent')?.get('label'),
            locationGrandparent: this.get('location')?.get('parent')?.get('parent')?.get('label'),
            town: this.get('town')?.get('shortCode'),
            lat: getLatLng('lat'),
            lng: getLatLng('lng'),
            locationNotes: this.get('locationNotes')?.map(note => note.get('text')).join('; ') || '',
            date: CommonUtils.formatDate(date, this.get('dateSpecificity')),
            observedBy: this.get('observedBy')?.get('firstName') + ' ' + this.get('observedBy')?.get('lastName'),
            eventType: this.get('event')?.get('type')?.get('shortCode'),
            eventName: this.get('event')?.get('name'),

        }
    }

    inspect = () => {
        const validationData = {
            name: this.get('name')?.id || this.get('name'),
            substantiation: this.get('substantiation')?.id || this.get('substantiation'),
            observedBy: this.get('observedBy')?.get?.('person')?.id || this.get('observedBy'),
            reviewedBy: this.get('reviewedBy')?.get?.('person')?.id || this.get('reviewedBy'),
            locDeterminization: this.get('locDeterminization')?.id || this.get('locDeterminization'),
            substrate: this.get('substrate')?.id || this.get('substrate'),
            quality: this.get('quality')?.id || this.get('quality'),
            town: this.get('town')?.id || this.get('town'),
            isDetected: this.get('isDetected'),
            flag: this.get('flag'),
            date: this.get('date'),
            event: this.get('event')?.id || this.get('event'),
            location: this.get('location')?.id || this.get('location'),
            lat: this.get('lat') ? parseFloat(this.get('lat')) : null,
            lng: this.get('lng') ? parseFloat(this.get('lng')) : null
        };
        return Observation.VALIDATION_SCHEMA.validate(validationData, { abortEarly: false });
    }
}
  
if (global.Parse.Object.registerSubclass) {
    global.Parse.Object.registerSubclass('Observation', Observation);
}